/**
 * Shared service for shift plan API operations
 */
import { store } from '../../store';
import { selectStationCode } from '../../store/slices/shiftPlanSlice';
import { apiRequest } from '../apiMiddleware';
import { getUserName } from '../boson/bosonClient';
import { API_GATEWAY_PROCESS_NAME, PlanStatus } from '../../utils/constants';
import {
  PlanType,
  PlanVersion,
  ShiftType,
  CreateShiftPlanRequest,
  CreateShiftPlanResponse,
  GetAllShiftPlansRequest,
  GetAllShiftPlansResponse,
} from '../../types/shiftPlan';
import { showError } from '../../store/slices/errorSlice';
import {
  GENERATE_SHIFT_PLAN_API,
  GENERATE_SHIFT_ASSIGNMENT_API,
  GET_ALL_SHIFT_PLANS_API,
  logOperationalMetric,
  MetricType,
  RCC_SHIFT_PLAN_GENERATION_PAGE,
  RCC_SHIFT_PLAN_SHIFT_ASSIGNMENT_PAGE,
} from '../../utils/metric-utils';

/**
 * Common error handler for createShiftPlan
 */
const handleCreateShiftPlanError = (errorSource: any): CreateShiftPlanResponse => {
  const errorMessage =
    errorSource.errorResponse ||
    errorSource.message ||
    'Unable to generate new plan from the server. Please try again later.';

  store.dispatch(
    showError({
      title: 'Failed to generate new Plan',
      message: errorMessage,
    })
  );

  return {
    planId: '',
    status: 'FAILED' as PlanStatus,
  };
};

/**
 * Generate shift plan by calling the API
 */
export const createShiftPlan = async (
  ofdDate: string,
  shiftType: ShiftType,
  planType: PlanType,
  planVersion: PlanVersion,
  takeOverPlan = false
): Promise<CreateShiftPlanResponse> => {
  const state = store.getState();
  const startTime = Date.now();
  const stationCode = selectStationCode(state);

  const metricPage =
    planVersion === PlanVersion.RELO_STATION_ASSIGNMENT_V1
      ? RCC_SHIFT_PLAN_SHIFT_ASSIGNMENT_PAGE
      : RCC_SHIFT_PLAN_GENERATION_PAGE;
  const metricApi =
    planVersion === PlanVersion.RELO_STATION_ASSIGNMENT_V1
      ? GENERATE_SHIFT_ASSIGNMENT_API
      : GENERATE_SHIFT_PLAN_API;

  try {
    const userId: string = (await getUserName()) as string;

    if (!stationCode) {
      store.dispatch(
        showError({
          title: 'Warehouse ID Required',
          message: 'Please select a valid warehouse ID from the dropdown',
        })
      );
      return { planId: '', status: 'FAILED' as PlanStatus };
    }

    if (!userId || userId.length < 1) {
      store.dispatch(
        showError({
          title: 'User ID Required',
          message: 'User ID not recognized, please refresh the page and try again',
        })
      );
      return { planId: '', status: 'FAILED' as PlanStatus };
    }

    const requestBody: CreateShiftPlanRequest = {
      ofdDate,
      planType,
      clockWindowName: shiftType,
      nodeId: stationCode,
      planVersion,
      createdBy: userId,
      takeOverPlan,
    };
    const requestPayload = {
      resourcePath: '/createShiftPlan',
      httpMethod: 'post',
      processName: API_GATEWAY_PROCESS_NAME,
      requestBody,
    };

    const response = await apiRequest(requestPayload);

    if (response.status && response.status >= 400) {
      logOperationalMetric(
        startTime,
        stationCode,
        metricPage,
        metricApi,
        MetricType.FAILED,
        response
      );
      return handleCreateShiftPlanError(response);
    }

    logOperationalMetric(startTime, stationCode, metricPage, metricApi, MetricType.SUCCESS);

    return {
      planId: response.data.planId,
      status: response.data.status as PlanStatus,
      errorDetails: response.data.errorDetails,
    };
  } catch (error: any) {
    console.error('Error generating shift plan:', error);
    logOperationalMetric(
      startTime,
      stationCode || '',
      metricPage,
      metricApi,
      MetricType.FAILED,
      error
    );
    return handleCreateShiftPlanError(error);
  }
};

/**
 * Get all shift plans for a given date and shift type
 */
export const getAllShiftPlans = async (
  date: string,
  shiftType: ShiftType,
  planType?: PlanType
): Promise<GetAllShiftPlansResponse | null> => {
  const startTime = Date.now();
  try {
    const state = store.getState();
    const stationCode = selectStationCode(state);

    if (!stationCode) {
      store.dispatch(
        showError({
          title: 'Warehouse ID Required',
          message: 'Please select a valid warehouse ID from the dropdown',
        })
      );
      return null;
    }

    const requestBody: GetAllShiftPlansRequest = {
      nodeId: stationCode,
      date,
      shiftType,
      planType
    };

    const requestPayload = {
      resourcePath: '/getAllShiftPlans',
      httpMethod: 'post',
      processName: API_GATEWAY_PROCESS_NAME,
      requestBody,
    };

    const response = await apiRequest(requestPayload);
    logOperationalMetric(
      startTime,
      stationCode,
      RCC_SHIFT_PLAN_GENERATION_PAGE,
      GET_ALL_SHIFT_PLANS_API,
      MetricType.SUCCESS
    );
    return response.data.data;
  } catch (e: any) {
    console.log('Error fetching all shift plans');
    const state = store.getState();
    const stationCode = selectStationCode(state) || '';
    logOperationalMetric(
      startTime,
      stationCode,
      RCC_SHIFT_PLAN_GENERATION_PAGE,
      GET_ALL_SHIFT_PLANS_API,
      MetricType.FAILED,
      e
    );
    store.dispatch(
      showError({
        title: 'Failed to fetch all shift plans',
        message: 'Unable to fetch all shift plans. Please try again later.',
      })
    );
    return null;
  }
};
